import { saveAs } from 'file-saver'
import fileDownload from 'js-file-download'
import JSZip from 'jszip'
import isEmpty from 'lodash/isEmpty'
import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

import { imageType } from '@/pages/getRecipe/allInOne/utils/constants'
import { Project } from '@/pages/projects'
import { type CanvasImage, PLACEMENT } from '@/store/appSlice'

import {
  DIGITAL_TRY_IN_AREAS,
  RECIPE_TYPE_OPTIONS,
  RECIPE_WORKFLOW_COLOR,
  illuminants,
} from './constants'

export const getInitialImageColorCheckerMarker = (type: any) => {
  const squareAreaAttrs = {
    xy: null,
    imageClickXY: null,
    rgb: null,
    squareSelectedSize: 1,
    clickPixelPos: { x: 0, y: 0 },
    lab: ['-', '-', '-'],
    deleteButton: false,
    toolTip: false,
  }
  if (type === 'raw') {
    squareAreaAttrs.squareSelectedSize = 0
  }
  const initialImageColorCheckerMarker = {
    left: {
      [DIGITAL_TRY_IN_AREAS.CERVICAL]: squareAreaAttrs,
      [DIGITAL_TRY_IN_AREAS.MIDDLE]: squareAreaAttrs,
      [DIGITAL_TRY_IN_AREAS.INCISAL]: squareAreaAttrs,
    },
    right: {
      [DIGITAL_TRY_IN_AREAS.CERVICAL]: squareAreaAttrs,
      [DIGITAL_TRY_IN_AREAS.MIDDLE]: squareAreaAttrs,
      [DIGITAL_TRY_IN_AREAS.INCISAL]: squareAreaAttrs,
    },
  }
  return initialImageColorCheckerMarker
}

export const CIELABdeltaE = (labLeft: number[], labRight: number[]) => {
  const deltaE = Math.sqrt(
    (labLeft[0] - labRight[0]) ** 2 +
      (labLeft[1] - labRight[1]) ** 2 +
      (labLeft[2] - labRight[2]) ** 2,
  ).toFixed(2)
  return deltaE
}

export const CIELABdeltaE94 = (
  LAB1: number[],
  LAB2: number[],
  kL = 1.0,
  K1 = 0.045,
  K2 = 0.015,
) => {
  const cab1 = Math.sqrt(LAB1[1] * LAB1[1] + LAB1[2] * LAB1[2])
  const cab2 = Math.sqrt(LAB2[1] * LAB2[1] + LAB2[2] * LAB2[2])

  const delta_L = LAB1[0] - LAB2[0]
  const delta_a = LAB1[1] - LAB2[1]
  const delta_b = LAB1[2] - LAB2[2]

  const delta_C = cab1 - cab2
  const delta_H = Math.sqrt(
    Math.max(delta_a * delta_a + delta_b * delta_b - delta_C * delta_C, 0.0),
  )

  const cab = Math.sqrt(cab1 * cab2)
  const Sc = 1.0 + K1 * cab
  const Sh = 1.0 + K2 * cab

  const delta_L_corr = delta_L / kL
  const delta_C_corr = delta_C / Sc
  const delta_H_corr = delta_H / Sh

  const dE_94 = Math.sqrt(
    delta_L_corr * delta_L_corr +
      delta_C_corr * delta_C_corr +
      delta_H_corr * delta_H_corr,
  )

  return dE_94.toFixed(2)
}

export const onWhichScale = (
  stageW: number,
  stageH: number,
  imageW: number,
  imageH: number,
) => {
  const scaleByW = stageW / imageW
  const scaleByH = stageH / imageH
  const op = {
    scale: scaleByW < scaleByH ? scaleByW : scaleByH,
    scaleBy: scaleByW < scaleByH ? 'width' : 'height',
  }
  return op
}
export const getAvgRGB = (pixelsData: any, size: any) => {
  let r = 0,
    g = 0,
    b = 0
  for (let i = 0; i < pixelsData?.length; i += 4) {
    r += pixelsData[i]
    g += pixelsData[i + 1]
    b += pixelsData[i + 2]
  }
  return {
    r: r / size,
    g: g / size,
    b: b / size,
  }
}

export const getCanvasPixelPosition = (stageRef: React.RefObject<any>) => {
  if (!stageRef?.current) {
    return { x: 0, y: 0 }
  }
  const stage = stageRef.current
  const pointerPos = stage.getPointerPosition()
  if (!pointerPos) {
    return { x: 0, y: 0 }
  }
  const stageW = stage.width()
  const stageH = stage.height()
  const canvas = stage.children?.[0]?.canvas?._canvas

  if (!canvas) {
    return { x: 0, y: 0 }
  }
  const { width: canvasW, height: canvasH } = canvas

  return {
    x: Number(((pointerPos.x * canvasW) / stageW).toFixed(2)), // Convert to number
    y: Number(((pointerPos.y * canvasH) / stageH).toFixed(2)), // Convert to number
  }
}

export const getStageClickPosition = (stageRef: any) => {
  const pos = stageRef.current.getRelativePointerPosition()
  pos.x = pos.x
  pos.y = pos.y + 1
  return pos
}

export const getImagePositionOffset = (imageRef: any) => {
  return {
    x: imageRef.current.attrs.x,
    y: imageRef.current.attrs.y,
  }
}

export const getStageClickImageOffsetPosition = (
  stageRef: any,
  imageRef: any,
) => {
  const stage = stageRef.current
  const imageNode = imageRef.current
  if (!stage || !imageNode) return

  const pointer = stage.getPointerPosition()
  if (!pointer) return

  const transform = stage.getAbsoluteTransform().copy()
  transform.invert()
  const imagePoint = transform.point(pointer)

  const imageX = imagePoint.x - imageNode.x() + 50
  const imageY = imagePoint.y - imageNode.y()
  return { x: imageX, y: imageY }
}

export const getClickPositionOnRawImage = (
  stageRef: any,
  imageRef: any,
  selectedImage: any,
) => {
  let pos = {}
  const position = getStageClickImageOffsetPosition(stageRef, imageRef)
  // let rawImageW = selectedImage.rawWidth;
  // let rawImageH = selectedImage.rawHeight;
  const stageW = stageRef.current.width()
  const stageH = stageRef.current.height()
  const scaleObj = onWhichScale(
    stageRef.current.width(),
    stageRef.current.height(),
    imageRef.current.width(),
    imageRef.current.height(),
  )
  const scaleBy = scaleObj.scaleBy
  const scale =
    scaleBy === 'width'
      ? selectedImage.rawWidth / stageW
      : selectedImage.rawHeight / stageH
  pos = {
    x: (position.x * scale).toFixed(2),
    y: (position.y * scale).toFixed(2),
  }
  return pos
}

const matrixMultiply3x3 = (a: any, b: any) => {
  let d,
    e,
    f,
    c: any = [[]]
  for (d = 0; d < 3; d += 1)
    for (e = 0; e < 3; e += 1)
      for (0 === e && (c[d] = []), c[d][e] = 0, f = 0; f < 3; f += 1)
        c[d][e] += a[d][f] * b[f][e]
  return c
}

const matrixInvert3x3 = (a: any) => {
  let b,
    d,
    e,
    c: any = [[]]
  for (d = 0; d < 3; d += 1) c[d] = []
  for (
    c[0][0] = a[1][1] * a[2][2] - a[1][2] * a[2][1],
      c[1][0] = -1 * (a[1][0] * a[2][2] - a[1][2] * a[2][0]),
      c[2][0] = a[1][0] * a[2][1] - a[1][1] * a[2][0],
      c[0][1] = -1 * (a[0][1] * a[2][2] - a[0][2] * a[2][1]),
      c[1][1] = a[0][0] * a[2][2] - a[0][2] * a[2][0],
      c[2][1] = -1 * (a[0][0] * a[2][1] - a[0][1] * a[2][0]),
      c[0][2] = a[0][1] * a[1][2] - a[0][2] * a[1][1],
      c[1][2] = -1 * (a[0][0] * a[1][2] - a[0][2] * a[1][0]),
      c[2][2] = a[0][0] * a[1][1] - a[0][1] * a[1][0],
      b = a[0][0] * c[0][0] + a[0][1] * c[1][0] + a[0][2] * c[2][0],
      d = 0;
    d < 3;
    d += 1
  )
    for (e = 0; e < 3; e += 1) c[d][e] = c[d][e] / b
  return c
}

const bradfordTransform = (a: any, b: any) => {
  let g,
    c = [
      [0.8951, 0.2664, -0.1614],
      [-0.7502, 1.7135, 0.0367],
      [0.0389, -0.0685, 1.0296],
    ],
    d: any = [],
    e: any = [],
    f = [
      [0, 0, 0],
      [0, 0, 0],
      [0, 0, 0],
    ]
  for (
    d = matrixVectorProduct(c, a), e = matrixVectorProduct(c, b), g = 0;
    g < 3;
    g += 1
  )
    f[g][g] = e[g] / d[g]
  return matrixMultiply3x3(matrixMultiply3x3(matrixInvert3x3(c), f), c)
}

const matrixVectorProduct = (a: any, b: any) => {
  let e,
    f,
    c = b.length,
    d = []
  if (a[0].length !== c)
    return alert('Error computing matrix vector products'), null
  for (e = 0; e < c; e += 1)
    for (d[e] = 0, f = 0; f < c; f += 1) d[e] += a[e][f] * b[f]
  return d
}

const adaptXyz = (a: any, b: any, c: any) => {
  return matrixVectorProduct(bradfordTransform(b, c), a)
}

const convertXyz = (a: any) => {
  return adaptXyz(a, illuminants, illuminants)
}

const fixXyz = (a: any) => {
  const b =
    a[0] > illuminants[0] || a[1] > illuminants[1] || a[2] > illuminants[2]
  if (((a = convertXyz(a)), !b))
    for (let c = 0; c < 3; c++) a[c] > illuminants[c] && (a[c] = illuminants[c])
  return a
}
const rgbMatrix = (a: any, b: any, c: any, d: any) => {
  let h,
    i,
    e: any = [[]],
    f: any = [[]],
    g: any = []
  for (h = 0; h < 3; h += 1) {
    e[h] = []
    f[h] = []
  }
  f[0][0] = a[0] / a[1]
  f[1][0] = 1
  f[2][0] = (1 - a[0] - a[1]) / a[1]
  f[0][1] = b[0] / b[1]
  f[1][1] = 1
  f[2][1] = (1 - b[0] - b[1]) / b[1]
  f[0][2] = c[0] / c[1]
  f[1][2] = 1
  f[2][2] = (1 - c[0] - c[1]) / c[1]
  g[0] = d[0] / d[1]
  g[1] = 1
  g[2] = (1 - d[0] - d[1]) / d[1]
  const j: any = matrixVectorProduct(matrixInvert3x3(f), g)
  for (h = 0; h < 3; h += 1) for (i = 0; i < 3; i += 1) e[h][i] = j[i] * f[h][i]
  return e
}

const xyzToLab = (a: any, b: any) => {
  let h,
    c = 216 / 24389,
    d = 24389 / 27,
    e = [],
    g = [],
    i = []
  for (h = 0; h < 3; h += 1) {
    i[h] = a[h] / b[h]
    i[h] > c ? (e[h] = Math.pow(i[h], 1 / 3)) : (e[h] = (d * i[h] + 16) / 116)
  }
  return (
    (g[0] = Math.round(100 * (116 * e[1] - 16)) / 100),
    (g[1] = Math.round(500 * (e[0] - e[1]) * 100) / 100),
    (g[2] = Math.round(200 * (e[1] - e[2]) * 100) / 100),
    g
  )
}
const rgbToXyz = (a: any, b: any, c: any, d: any, e: any, f: any) => {
  const g = []
  g[0] = e[0] / e[1]
  g[1] = 1
  g[2] = (1 - e[0] - e[1]) / e[1]
  const h = rgbMatrix(b, c, d, e),
    i = matrixVectorProduct(h, a)
  return adaptXyz(i, g, f)
}

const xyzChromaticity = (a: any) => {
  const b = []
  return (
    (b[0] = a[0] / (a[0] + a[1] + a[2])),
    (b[1] = a[1] / (a[0] + a[1] + a[2])),
    b
  )
}

const sRGBtoXYZ = (a: any, b: any) => {
  var h,
    c = [],
    d = [0.64, 0.33],
    e = [0.3, 0.6],
    f = [0.15, 0.06],
    g = [0.95047, 1, 1.08883]
  for (h = 0; h < 3; h += 1) {
    c[h] = a[h] / 255
    c[h] > 0.04045
      ? (c[h] = Math.pow((c[h] + 0.055) / 1.055, 2.4))
      : (c[h] = c[h] / 12.92)
  }

  return rgbToXyz(c, d, e, f, xyzChromaticity(g), b)
}

export const rgb_to_lab_main = (R: any, G: any, B: any) => {
  R = parseInt(R, 10)
  G = parseInt(G, 10)
  B = parseInt(B, 10)

  let f, g
  const a = illuminants
  const b = illuminants
  const e = [R, G, B]

  g = [e[0], e[1], e[2]]
  f = fixXyz(sRGBtoXYZ(g, a))
  const Lab = xyzToLab(f, b)

  return {
    l: Number(Lab[0]).toFixed(2),
    a: Number(Lab[1]).toFixed(2),
    b: Number(Lab[2]).toFixed(2),
  }
}

export const rgb_to_lab = (r: any, g: any, b: any) => {
  r = parseInt(r, 10)
  g = parseInt(g, 10)
  b = parseInt(b, 10)

  const k = 24389.0 / 27.0 // k & e fixes the CIE floating point error
  const e = 216.0 / 24389.0

  var vr = r / 255.0
  var vg = g / 255.0
  var vb = b / 255.0
  if (vr > 0.04045) {
    vr = Math.pow((vr + 0.055) / 1.055, 2.4)
  } else {
    vr = vr / 12.92
  }
  if (vg > 0.04045) {
    vg = Math.pow((vg + 0.055) / 1.055, 2.4)
  } else {
    vg = vg / 12.92
  }
  if (vb > 0.04045) {
    vb = Math.pow((vb + 0.055) / 1.055, 2.4)
  } else {
    vb = vb / 12.92
  }
  var vx =
    vr * 41.23955889674142 + vg * 35.75834307637148 + vb * 18.049264738170155
  var vy =
    vr * 21.258623078559552 + vg * 71.51703037034109 + vb * 7.22004986433362
  var vz =
    vr * 1.9297215491746935 + vg * 11.91838645808485 + vb * 95.04971251315798
  vx /= 95.04285453772 // we are @ wp=D65 with sRGB
  vy /= 100.0
  vz /= 108.890037079813
  if (vx > e) {
    vx = Math.pow(vx, 1.0 / 3.0)
  } else {
    vx = (k * vx + 16.0) / 116.0
  }
  if (vy > e) {
    vy = Math.pow(vy, 1.0 / 3.0)
  } else {
    vy = (k * vy + 16.0) / 116.0
  }
  if (vz > e) {
    vz = Math.pow(vz, 1.0 / 3.0)
  } else {
    vz = (k * vz + 16.0) / 116.0
  }

  return {
    l: Number(116.0 * vy - 16.0).toFixed(2),
    a: Number(500.0 * (vx - vy)).toFixed(2),
    b: Number(200.0 * (vy - vz)).toFixed(2),
  }
}

interface Option {
  order: number
  [key: string]: any
}

export const getOptions = (metadata: Record<string, any>, key: string) =>
  metadata?.[key as keyof typeof metadata]
    ?.flat()
    ?.sort((a: Option, b: Option) => a.order - b.order)

export const isImageCalibrated = (image: any) => {
  return !!image?.calibrated_image || image?.type !== imageType.RAW
}

export const getDehazedImageUrl = (image: CanvasImage | null) => {
  if (isEmpty(image)) return ''
  if (!image?.dehazed_image) return ''
  return !image?.recipe_data?.dehazingReseted ? image?.dehazed_image : ''
}

export const isNewDehazeEnabled = (image: CanvasImage | null) => {
  if (isEmpty(image)) return false
  if (!image?.dehazed_image) return false
  return isEmpty(image?.recipe_data)
    ? true
    : !!image?.recipe_data?.dehazingReseted
}

export const isDehazeIconVisible = (image: CanvasImage | null) => {
  if (isEmpty(image)) return false
  if (!image?.dehazed_image) return false
  return !image?.recipe_data?.dehazingReseted
}

export const useRecipeCheck = (path: string, recipe: any) => {
  const location = useLocation()

  return useMemo(() => {
    return (
      location.pathname === path &&
      !isEmpty(recipe) &&
      (!isEmpty(recipe?.[PLACEMENT.LEFT]) ||
        !isEmpty(recipe?.[PLACEMENT.RIGHT]))
    )
  }, [location.pathname, recipe])
}

export function hasNonEmptyOutputDualCanvas(recipeOutput: any): boolean {
  return !!(
    recipeOutput &&
    Object.values(recipeOutput).some((side) =>
      Object.values(side).some(
        (region: any) => region.output && Object.keys(region.output).length > 0,
      ),
    )
  )
}

export function hasNonEmptyOutputSingleCanvas(
  recipeOutput: Record<string, any> | null | undefined,
  imageId: string | undefined,
): boolean {
  if (!imageId || !recipeOutput?.[imageId]) return false

  return Object.values(recipeOutput[imageId]).length > 0
}
type OutputRecord = Record<string, Record<string, any>>

interface GetTooltipPlacementParams {
  keyName: string
  currentX: number | null
  currentY: number | null
  open: boolean
  canvasImageId: string | undefined
  output: OutputRecord
  prevOutputRef: React.MutableRefObject<OutputRecord | null>
  lastTooltipMap: Record<string, { x: number; y: number }>
}

export function getTooltipPlacement({
  keyName,
  currentX,
  currentY,
  open,
  canvasImageId,
  output,
  prevOutputRef,
  lastTooltipMap,
}: GetTooltipPlacementParams): 'left' | 'right' | null {
  const currentOutput = output

  const prevOutput = prevOutputRef.current?.[canvasImageId]?.[keyName]

  const wasPreviouslyEmpty = !prevOutput || Object.keys(prevOutput).length === 0
  const isNowPresent = currentOutput && Object.keys(currentOutput).length > 0

  if (wasPreviouslyEmpty && isNowPresent) {
    delete lastTooltipMap[keyName]
  }

  prevOutputRef.current = output

  if (!isNowPresent) {
    delete lastTooltipMap[keyName]
    return null
  }

  if (open && currentX !== null && currentY !== null) {
    const lastEntry = Object.entries(lastTooltipMap).find(([otherKey, pos]) => {
      if (otherKey === keyName) return false
      return (
        Math.abs(pos.x - currentX) < 150 && Math.abs(pos.y - currentY) < 220
      )
    })
    if (!lastTooltipMap[keyName]) {
      lastTooltipMap[keyName] = { x: currentX, y: currentY }
      if (lastEntry) {
        return 'left'
      } else {
        return 'right'
      }
    }
  }

  return null
}

export const getStatusStyle = (status: Project['status']) => {
  switch (status) {
    case 'active':
      return { bgColor: '#008807', textColor: '#fff' }
    case 'scheduled':
      return { bgColor: '#5D5FEF', textColor: '#fff' }
    case 'archive':
      return { bgColor: '#8080808C', textColor: '#fff' }
    default:
      return { bgColor: '#000', textColor: '#fff' }
  }
}

export const downloadImages = async (
  images: { name: string; url: string }[],
) => {
  if (images.length === 1) {
    const image = images[0]
    const response = await fetch(image.url)
    const blob = await response.blob()
    saveAs(blob, image.name)
  } else {
    const zip = new JSZip()

    await Promise.all(
      images.map(async (image) => {
        const response = await fetch(image.url)
        const blob = await response.blob()
        zip.file(image.name, blob)
      }),
    )

    const content = await zip.generateAsync({ type: 'blob' })
    saveAs(content, 'images.zip')
  }
}

export const getRecipeLabelDetails = ({
  workflow,
  type,
}: {
  workflow: string | null
  type: string
}) => {
  const workflowObj = RECIPE_WORKFLOW_COLOR.find((item) => item[workflow || ''])
  const workflowColor =
    workflowObj?.[workflow || ''] ||
    (workflow === null &&
    ['generic', 'analog_monolithic', 'analog_layering'].includes(type)
      ? '#2B66F6'
      : 'rgba(255, 90, 31, 1)')

  let typeLabel =
    RECIPE_TYPE_OPTIONS.find((opt) => opt.value === type)?.label || type

  if (workflow === 'staining_studio' && type === 'staining_fullmonolithic') {
    typeLabel = 'Staining Studio: Monolithic/ Color Corrections'
  } else if (
    [
      'pfm_crown',
      'full_layering',
      'full_monolithic',
      'micro_layering',
    ].includes(workflow || '') &&
    type === 'staining_fullmonolithic'
  ) {
    typeLabel = 'Staining Studio'
  } else if (
    workflow === 'full_layering' &&
    [
      'framework_crown',
      'framework_veneer',
      'framework_maryland_bridge',
      'framework_implant_crown',
      'framework_monolithic_crown',
    ].includes(type)
  ) {
    typeLabel = 'Framework'
  } else if (
    workflow === 'staining_studio' &&
    type === 'staining_color_model'
  ) {
    typeLabel = 'ColorModel/ Stump Shade'
  } else if (workflow === 'gingiva' && type === 'staining_gingiva') {
    typeLabel = 'Staining'
  } else if (workflow === 'gingiva' && type === 'gingiva') {
    typeLabel = 'Ceramic'
  }
  const workflowName = workflowObj?.name || workflow
  const labelText =
    workflowName && workflowName !== 'Staining Studio'
      ? `${workflowName}: ${typeLabel}`
      : typeLabel

  return {
    typeLabel,
    workflowColor,
    labelText,
  }
}

type SaveRecipeOptions = {
  caseId: string
  canvasImage?: any
  dualCanvasImage?: any
  recipeName: string
  output: any
  recipeType?: string
  workflow: string | null
  mutationFn: any
  onSuccessMessage?: string
  onErrorMessage?: string
  closeDialog?: () => void
  extraOutput?: Record<string, any>
  inputType?: 'single' | 'dual'
  isPdf?: boolean
}

export const saveRecipeGeneric = async ({
  caseId,
  canvasImage,
  dualCanvasImage,
  recipeName,
  output,
  recipeType,
  workflow,
  mutationFn,
  isPdf = false,
  onSuccessMessage,
  onErrorMessage,
  closeDialog,
  extraOutput = {},
  inputType = 'single',
}: SaveRecipeOptions & { isPdf?: boolean }) => {
  const successMsg =
    onSuccessMessage ||
    (isPdf ? 'PDF downloaded successfully.' : 'Recipe saved successfully.')
  const errorMsg =
    onErrorMessage ||
    (isPdf ? 'Failed to download PDF.' : 'Failed to save recipe.')

  try {
    const input =
      inputType === 'dual'
        ? {
            left_key_image: dualCanvasImage?.left?.key_image,
            right_key_image: dualCanvasImage?.right?.key_image,
          }
        : {
            key_image: canvasImage?.key_image,
          }

    const data = {
      type: recipeType,
      input,
      output: {
        ...output,
        ...extraOutput,
      },
      workflow,
      name: recipeName,
    }

    const response = await mutationFn({ id: caseId, data }).unwrap()

    const fileName = getRecipeLabelDetails({
      workflow,
      type: recipeType,
    })?.labelText

    if (isPdf) {
      const blob =
        response instanceof Blob
          ? response
          : new Blob([response], { type: 'application/pdf' })
      fileDownload(blob, `${fileName}.pdf`)
    }

    toast.success(successMsg)
    if (closeDialog) closeDialog()
  } catch (err) {
    toast.error(errorMsg)
  }
}

export const prepareSelectedDentinOutput = (
  recipeOutput: any,
  recipeIndex: any,
): Record<string, any> => {
  const formattedOutput: Record<string, any> = {}

  if (!recipeOutput || !recipeIndex) return formattedOutput

  for (const region in recipeOutput) {
    const regionOutput = recipeOutput[region]
    const selectedIndex = recipeIndex[region] ?? 0

    const selectedLayer1 = regionOutput.recipe_layer1?.[selectedIndex] || null
    const selectedLayer2 = regionOutput.recipe_layer2?.[selectedIndex] || null

    formattedOutput[region] = {
      ...regionOutput,
      recipe_layer1: selectedLayer1 ? [selectedLayer1] : [],
      recipe_layer2: selectedLayer2 ? [selectedLayer2] : [],
    }
  }

  return formattedOutput
}
